import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Eye, Trash2, Search, LogIn, RefreshCw, Filter, TrendingUp, TrendingDown, ArrowRightLeft } from "lucide-react";
import { getHistoryEntries, clearHistory, type HistoryEntry } from "@/lib/watchlistStore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getDecisionHistory } from "@/lib/cloudServices";
import DecisionCompare from "@/components/DecisionCompare";

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function daysSince(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / (24 * 60 * 60 * 1000));
}

// Aging signal for a decision
function getAgingSignal(entry: HistoryEntry): { text: string; className: string; icon: typeof TrendingUp } | null {
  const days = daysSince(entry.decidedAt);
  if (days < 7) return null;
  if (days >= 30) {
    return { text: "Worth rechecking", className: "text-score-mid", icon: ArrowRightLeft };
  }
  if (days >= 14) {
    return { text: "Market may have shifted", className: "text-muted-foreground", icon: TrendingUp };
  }
  return null;
}

// Normalize cloud decision row to local HistoryEntry format
function cloudToHistory(row: Record<string, unknown>): HistoryEntry {
  return {
    id: row.id as string,
    query: row.raw_query as string,
    category: (row.category as string) ?? "",
    country: (row.market as string) ?? undefined,
    winnerName: (row.winner_product_name as string) ?? "",
    winnerPrice: (row.winner_price as string) ?? "",
    winnerLabel: (row.winner_label as string) ?? "",
    confidence: (row.confidence_level as string) ?? "",
    coverage: "demo",
    decidedAt: (row.created_at as string) ?? new Date().toISOString(),
  };
}

export default function HistoryPage() {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [compareEntry, setCompareEntry] = useState<HistoryEntry | null>(null);
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();

  useEffect(() => {
    async function load() {
      if (user) {
        const { data } = await getDecisionHistory(user.id);
        const cloudEntries = data.map(cloudToHistory);
        const localEntries = getHistoryEntries();
        const allIds = new Set(cloudEntries.map((e) => e.id));
        const merged = [...cloudEntries, ...localEntries.filter((e) => !allIds.has(e.id))];
        setEntries(merged);
      } else {
        setEntries(getHistoryEntries());
      }
      setLoading(false);
    }
    load();
  }, [user]);

  const handleClear = () => {
    clearHistory();
    setEntries([]);
  };

  // Get unique categories for filter
  const categories = [...new Set(entries.map(e => e.category).filter(Boolean))];

  // Filter entries
  const filtered = entries.filter((e) => {
    if (searchTerm && !e.query.toLowerCase().includes(searchTerm.toLowerCase()) && !e.winnerName.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterCategory && e.category !== filterCategory) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-safe pb-[100px] md:pb-[72px]">
      <div className="max-w-2xl mx-auto px-5 pt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-display font-bold text-foreground mb-0.5">Decision Memory</h1>
            <p className="text-sm text-muted-foreground font-body">
              {entries.length} decision{entries.length !== 1 ? "s" : ""} logged.
            </p>
          </div>
          {entries.length > 0 && !user && (
            <button
              onClick={handleClear}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-body text-muted-foreground hover:text-score-low transition-colors border border-border/40"
            >
              <Trash2 className="w-3 h-3" />Clear
            </button>
          )}
        </div>

        {/* Search & filter bar */}
        {entries.length > 0 && (
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search decisions..."
                className="w-full pl-8 pr-3 py-2 rounded-lg border border-border/40 bg-card text-xs font-body text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/30"
              />
            </div>
            {categories.length > 1 && (
              <select
                value={filterCategory ?? ""}
                onChange={(e) => setFilterCategory(e.target.value || null)}
                className="px-2.5 py-2 rounded-lg border border-border/40 bg-card text-[11px] font-body text-muted-foreground focus:outline-none"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {!user && isGuest && entries.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border border-primary/20 bg-primary/5 p-3.5 mb-4">
            <div className="flex items-center gap-2 mb-1.5">
              <LogIn className="w-3.5 h-3.5 text-primary" />
              <p className="text-xs font-body font-medium text-primary">Guest mode</p>
            </div>
            <p className="text-[11px] text-muted-foreground font-body">
              History is stored locally. Sign in to keep it permanently.
            </p>
            <button onClick={() => navigate("/auth")} className="text-[11px] text-primary font-body font-medium mt-1.5 hover:underline">
              Create account
            </button>
          </motion.div>
        )}

        {filtered.length === 0 && entries.length > 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground font-body">No matching decisions found.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Clock className="w-6 h-6 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="text-base font-display font-semibold text-foreground mb-1.5">No decisions yet</h3>
            <p className="text-muted-foreground font-body text-sm max-w-[260px] mx-auto mb-6">
              Your past decisions will appear here.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/")}
              className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-body text-sm"
            >
              Get the best option
            </motion.button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((entry, i) => {
              const entryDate = new Date(entry.decidedAt);
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);
              const weekAgo = new Date(today);
              weekAgo.setDate(weekAgo.getDate() - 7);

              let groupLabel = "";
              if (entryDate.toDateString() === today.toDateString()) groupLabel = "Today";
              else if (entryDate.toDateString() === yesterday.toDateString()) groupLabel = "Yesterday";
              else if (entryDate > weekAgo) groupLabel = "This week";
              else groupLabel = "Earlier";

              const prevEntry = i > 0 ? filtered[i - 1] : null;
              let showHeader = false;
              if (!prevEntry) {
                showHeader = true;
              } else {
                const prevDate = new Date(prevEntry.decidedAt);
                let prevGroup = "";
                if (prevDate.toDateString() === today.toDateString()) prevGroup = "Today";
                else if (prevDate.toDateString() === yesterday.toDateString()) prevGroup = "Yesterday";
                else if (prevDate > weekAgo) prevGroup = "This week";
                else prevGroup = "Earlier";
                showHeader = prevGroup !== groupLabel;
              }

              const aging = getAgingSignal(entry);
              const AgingIcon = aging?.icon;

              return (
                <div key={entry.id}>
                  {showHeader && (
                    <p className="text-[10px] text-muted-foreground/40 uppercase tracking-wider py-2 font-body">
                      {groupLabel}
                    </p>
                  )}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="rounded-xl border border-border/50 bg-card p-3.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <Search className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                          <span className="text-sm font-body text-foreground truncate">{entry.query}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-body">
                            <Eye className="w-2.5 h-2.5" />
                            {entry.winnerLabel}
                          </span>
                          <span className="text-[11px] font-body font-semibold text-foreground truncate">{entry.winnerName}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground font-body">
                          <span>{entry.winnerPrice}</span>
                          <span className="capitalize">{entry.confidence}</span>
                          {entry.country && <span>{entry.country}</span>}
                        </div>
                      </div>
                      <span className="text-[10px] text-muted-foreground font-body shrink-0">
                        {timeAgo(entry.decidedAt)}
                      </span>
                    </div>

                    {/* Aging signal */}
                    {aging && AgingIcon && (
                      <div className="flex items-center gap-1.5 mt-2 px-2 py-1 rounded-md bg-muted/15 border border-border/20">
                        <AgingIcon className={`w-3 h-3 ${aging.className}`} />
                        <span className={`text-[10px] font-body ${aging.className}`}>{aging.text}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-1.5 mt-2.5">
                      <button
                        onClick={() => navigate(`/?q=${encodeURIComponent(entry.query)}`)}
                        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-body text-muted-foreground hover:text-foreground hover:bg-muted/25 transition-colors"
                      >
                        <RefreshCw className="w-3 h-3" />Re-run
                      </button>
                      {daysSince(entry.decidedAt) >= 7 && (
                        <button
                          onClick={() => setCompareEntry(entry)}
                          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-body text-primary/70 hover:text-primary hover:bg-primary/10 transition-colors"
                        >
                          <ArrowRightLeft className="w-3 h-3" />Compare to now
                        </button>
                      )}
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Compare modal */}
      <AnimatePresence>
        {compareEntry && (
          <DecisionCompare
            decision={compareEntry}
            onClose={() => setCompareEntry(null)}
            onRerun={(q) => navigate(`/?q=${encodeURIComponent(q)}`)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
